import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MsalService } from '@azure/msal-angular';
import {
  BehaviorSubject,
  from,
  lastValueFrom,
  Observable,
  throwError,
} from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';

import { Asset } from '../interfaces/asset';

export interface FavoriteRequest {
  assetId: number;
  action: 'add' | 'remove';
}

@Injectable({
  providedIn: 'root',
})
export class FavoriteService {
  private favoritesSet = new Set<number>();

  public favorites$ = new BehaviorSubject<Set<number>>(this.favoritesSet);

  private isLoaded = false;
  private loadFavoritesPromise: Promise<number[]> | null = null;
  constructor(
    private http: HttpClient,
    private authService: MsalService,
  ) {}

  loadFavorites(): Observable<number[]> {
    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

        return this.http.get<number[]>(
          // HIER den neuen Endpoint eintragen!
          `${environment.appConfig.apiEndpoint}/getMyFavoriteIds`,
          { headers },
        );
      }),
      tap((ids: number[]) => {
        this.favoritesSet = new Set(ids);
        this.favorites$.next(this.favoritesSet);
      }),
    );
  }

  ensureFavoritesLoaded(): Promise<number[]> {
    if (this.isLoaded) {
      return Promise.resolve(Array.from(this.favoritesSet));
    }

    if (!this.loadFavoritesPromise) {
      this.loadFavoritesPromise = new Promise((resolve, reject) => {
        this.loadFavorites().subscribe({
          next: (ids) => {
            this.isLoaded = true;
            resolve(ids);
          },
          error: (err) => {
            this.loadFavoritesPromise = null;
            reject(err);
          },
        });
      });
    }

    return this.loadFavoritesPromise;
  }

  isLiked(assetId: number): boolean {
    return this.favoritesSet.has(assetId);
  }

  toggleFavorite(
    assetId: number,
    isAdding: boolean,
  ): Observable<{ message: string }> {
    if (isAdding) {
      this.favoritesSet.add(assetId);
    } else {
      this.favoritesSet.delete(assetId);
    }
    this.favorites$.next(this.favoritesSet);

    const body: FavoriteRequest = {
      assetId,
      action: isAdding ? 'add' : 'remove',
    };

    return this.getAccessToken().pipe(
      switchMap((token) => {
        const headers = new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        });
        return this.http.post<{ message: string }>(
          `${environment.appConfig.apiEndpoint}/toggleFavorite`,
          body,
          { headers },
        );
      }),
      catchError((err) => {
        if (isAdding) this.favoritesSet.delete(assetId);
        else this.favoritesSet.add(assetId);

        this.favorites$.next(this.favoritesSet);
        return throwError(() => err);
      }),
    );
  }

  private getAccessToken(): Observable<string> {
    const account = this.authService.instance.getAllAccounts()[0];
    if (!account)
      return throwError(() => new Error('Kein Benutzer eingeloggt.'));

    return from(
      this.authService.instance.acquireTokenSilent({
        scopes: environment.msalConfig.auth.scopes,
        account: account,
      }),
    ).pipe(map((response) => response.accessToken));
  }

  public getAccessTokenPromise(): Promise<string> {
    return lastValueFrom(this.getAccessToken());
  }
  getFavoriteAssetsChunked(
    offset: number,
    limit: number,
    httpInst: HttpClient,
    token: string,
  ): Promise<Asset[]> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    return lastValueFrom(
      httpInst.get<Asset[]>(
        `${environment.appConfig.apiEndpoint}/getFavoriteAssetsChunked?offset=${offset}&limit=${limit}`,
        { headers },
      ),
    );
  }
  async getApiFavoriteAssets(
    currentCycleObserv: BehaviorSubject<number>,
    dataChunkSize: number,
    getterFunction: (
      offset: number,
      limit: number,
      http: HttpClient,
      token: string,
    ) => Promise<Asset[]>,
    token: string,
    assets?: Asset[],
    filteredAssets?: Asset[],
  ): Promise<Asset[]> {
    return new Promise(async (resolve, reject) => {
      const cycle = currentCycleObserv.getValue();
      let currentCycle = cycle;

      const sub = currentCycleObserv.subscribe((next) => (currentCycle = next));

      let offset = 0;
      let cumulatedData: Asset[] = [];

      try {
        while (true) {
          const apiData = await getterFunction(
            offset,
            dataChunkSize,
            this.http,
            token,
          );

          if (currentCycle !== cycle) {
            sub.unsubscribe();
            reject(new Error('HttpInterrupt, Category been switched'));
            return; // Abbrechen
          }

          // 3. Wenn Daten da sind, ins UI pushen
          if (apiData.length > 0) {
            cumulatedData.push(...apiData);
            assets?.push(...apiData);
            filteredAssets?.push(...apiData);
          }

          offset += dataChunkSize;

          if (apiData.length < dataChunkSize) {
            break;
          }
        }

        sub.unsubscribe();
        resolve(cumulatedData);
      } catch (reason) {
        sub.unsubscribe();
        reject(reason);
      }
    });
  }
}
